import React, { useCallback, useContext, useMemo } from 'react';
import { SpaceSeparator } from '../..';
import { BillingPaymentMethod } from '../../../models';
import { PaymentMethodType } from '../../../enums';
import PaymentMethodIcon from './../PaymentMethodIcon/PaymentMethodIcon';
import { Context } from '../../AccelProvider/AccelProvider';
import moment from 'moment';
import { combineClasses } from '../../../utils';
import { PaymentMethodCardData } from '../../../models/Billing/Transaction/BillingTransaction';

export function getCardType(number: string): PaymentMethodType {
    // visa
    var re = new RegExp("^4");
    if (number.match(re) != null)
        return PaymentMethodType.Visa;

    if (/^220[0-4]/.test(number))
        return PaymentMethodType.MIR;

    // Mastercard 
    // Updated for Mastercard 2017 BINs expansion
    const firstFourDigits = Number(number.substring(0, 4));
    if (/^5[1-5]/.test(number) || (firstFourDigits >= 2221 && firstFourDigits <= 2720))
        return PaymentMethodType.Mastercard;

    // AMEX
    re = new RegExp("^3[47]");
    if (number.match(re) != null)
        return PaymentMethodType.AMEX;

    // Discover
    re = new RegExp("^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)");
    if (number.match(re) != null)
        return PaymentMethodType.Discover;

    // Diners
    re = new RegExp("^36");
    if (number.match(re) != null)
        return PaymentMethodType.Diners;

    // Diners - Carte Blanche
    re = new RegExp("^30[0-5]");
    if (number.match(re) != null)
        return PaymentMethodType.Diners;

    // JCB
    re = new RegExp("^35(2[89]|[3-8][0-9])");
    if (number.match(re) != null)
        return PaymentMethodType.JCB;

    // Visa Electron
    re = new RegExp("^(4026|417500|4508|4844|491(3|7))");
    if (number.match(re) != null)
        return PaymentMethodType.Visa;

    // unionpay
    re = new RegExp("^(62[0-9]{14,17})$");
    if (number.match(re) != null)
        return PaymentMethodType.UnionPay;

    return PaymentMethodType.Generic;
}

type Props = {
    method?: BillingPaymentMethod;
    cardData?: Partial<PaymentMethodCardData>;
    addressVisible?: boolean;
    className?: string;
}
const PaymentMethodPreview: React.FC<Props> = ({ method, cardData, addressVisible, className }) => {
    const { loc } = useContext(Context);

    const formatCardNumber = useCallback((last4: string) => {
        return `**** **** **** ${last4}`;
    }, []);

    const cardType = useMemo(() => method ? getCardType(method.cardNumber) : cardData?.type ? cardData.type : PaymentMethodType.Generic, [method, cardData]);
    const newlyAdded = useMemo(() => method ? moment().diff(method?.createdDate, 'minutes') < 5 : false, [method]);
    const cardNumber = useMemo(() => method ? method.cardNumber : formatCardNumber(cardData?.last4!), [method, cardData]);

    const cardTypeName = useMemo(() => cardType === PaymentMethodType.Generic
        ? loc.word('PaymentMethod.cardLabel', { default: 'Card' })
        : cardType, [cardType]);

    if (!method && !cardData)
        return null;

    return <div className={combineClasses('flex align-center', className)}>
        <PaymentMethodIcon type={cardType} />
        <div className='flex flex-col ml-10 lh-18'>
            <div className='fw-500'>{cardTypeName}</div>
            <SpaceSeparator separator='dot' itemClassName='color-secondary fs-12'>
                <span>{cardNumber}</span>
            </SpaceSeparator>
            {newlyAdded && <span className='color-success fs-10 lh-5 mt-5'>{loc.word('Billing.paymentMethodAddedLabel', { default: 'Newly added' })}</span>}
            {(method?.address && addressVisible === true) && <div className='fs-12 text-placeholder mt-5'>
                <SpaceSeparator separator='comma'>
                    {method.address.fullName}
                    {method.address.address}
                    {method.address.city}
                    {method.address.state}
                    {method.address.country}
                    {method.address.zipCode}
                    {method.address.company}
                    {method.address.note}
                </SpaceSeparator>
            </div>}
        </div>
    </div>
}

export default PaymentMethodPreview;